import { z } from 'zod';
import { IsoTimestamp, QuizId, SchoolId, SessionId, UserId } from './common.js';
import { SUBJECT_NAME_MAX } from './subject.js';
import { GRADE_LEVEL_NAME_MAX } from './grade-level.js';
export const ReportLifecycleState = z.enum([
    'notStarted',
    'inProgress',
    'submitted',
    'forceSubmitted',
    'awaitingGrading',
    'finalized',
]);
/** The answerable question types a quiz item can carry; also the question-table filter. */
export const ReportQuestionType = z.enum(['mcq', 'multipleAnswer', 'trueFalse', 'shortAnswer', 'numeric', 'matching', 'ordering', 'essay']);
/**
 * A multi-select filter value (AIMSIS-19671 cycle 8).
 *
 * The report filter panels send a multi-select as a comma-joined list, which a
 * `z.enum` rejects outright — so the list is validated member-by-member and
 * normalized to a canonical comma-joined string. Members are trimmed,
 * de-duplicated and sorted, so two equivalent selections produce the same
 * stored query and therefore the same print/export scope line.
 *
 * The canonical form is a plain `string` (not an array) because it is echoed
 * back to the client inside `ReportPage.filters` and re-sent verbatim by the
 * CSV/PDF export; a `string[]` would serialize to repeated keys and a
 * hand-written export URL could then disagree with the screen.
 */
function reportMultiSelect(values) {
    const allowed = values;
    return z.string().trim().min(1).max(400).transform((raw, ctx) => {
        const parts = [...new Set(raw.split(',').map((part) => part.trim()).filter((part) => part.length > 0))];
        const unknown = parts.filter((part) => !allowed.includes(part));
        if (unknown.length > 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `invalid value: ${unknown.join(', ')}` });
            return z.NEVER;
        }
        return parts.sort().join(',');
    });
}
/**
 * Quiz-status filter vocabulary for the Exam View.
 *
 * The ticket names Draft / Live / Finished / Archived, but the stored
 * `status` field is draft / scheduled / live / closed and archival is a
 * separate `archivedAt` stamp — so the filter accepts BOTH vocabularies and
 * maps them onto the stored shape (see `quizMatchesStatusFilter`). Accepting
 * the stored values too is deliberate: narrowing the enum to the four ticket
 * names would make every `scheduled` quiz unreachable under any selection,
 * which is the same unfilterable-value defect an earlier cycle was declined
 * for on `kind`.
 */
export const ReportQuizStatusFilter = z.enum(['draft', 'scheduled', 'live', 'closed', 'finished', 'archived']);
/**
 * Quiz-level completion classification, derived from the quiz's own participant
 * sessions (never stored). Mirrors the `lifecycleState` the quiz row already
 * reports, restricted to the states a whole quiz can actually be in.
 */
export const ReportCompletionStatus = z.enum(['notStarted', 'inProgress', 'awaitingGrading', 'finalized']);
/**
 * Quiz-level grading summary, derived from the quiz's own participant sessions:
 * `awaiting` when any answer still owes grading, `graded` when answers exist and
 * none do, `none` when the quiz has no answered attempt to grade.
 */
export const ReportGradingSummary = z.enum(['none', 'awaiting', 'graded']);
/**
 * Proctoring flags a participant session can carry, read from that session's
 * own `auditLog` event types.
 *
 * The set is a SUPERSET of the audit types that count as a proctor-visible
 * violation (`PROCTOR_PRIOR_VIOLATION_TYPES` in api/src/lib/integrity-policy.ts):
 * `split_screen` is recorded and listed for a proctor but deliberately excluded
 * from the counted set because it comes from a client-side geometry heuristic
 * with known false positives. Offering it as a filter is the point of that
 * exclusion — the reader can still narrow to it and weigh it in context. An
 * api-side test asserts this set covers every counted type, so the two cannot
 * drift apart silently.
 */
export const ReportProctoringFlag = z.enum([
    'visibility_hidden',
    'fullscreen_exit',
    'picture_in_picture_enter',
    'tab_blur',
    'paste',
    'time_drift',
    'split_screen',
]);
export const ReportQuery = z.object({
    tenantId: SchoolId.optional(),
    quizId: QuizId.optional(),
    classroomId: z.string().trim().min(1).max(64).optional(),
    scheduleId: z.string().trim().min(1).max(160).optional(),
    studentId: UserId.optional(),
    status: ReportLifecycleState.optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    after: z.string().max(256).optional(),
    search: z.string().trim().min(1).max(120).optional(),
    /** Question-table filters; applied before its page slice so totals agree. */
    questionSearch: z.string().trim().min(1).max(120).optional(),
    questionType: ReportQuestionType.or(z.literal('awaiting')).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    /**
     * Response format. `pdf` is a real server-rendered document (AIMSIS-19671
     * cycle 8), not the browser print dialog the ticket was bounced for: it
     * carries the same `filters` snapshot the JSON response does, so the exported
     * scope is the one the server applied rather than whatever the operator's
     * print settings produced.
     */
    format: z.enum(['json', 'csv', 'pdf']).default('json'),
    integrityPactStatus: z.enum(['not_required', 'acknowledged', 'missing']).optional(),
    /**
     * Exam View filters (AIMSIS-19671). These narrow the QUIZ dimension: they are
     * applied to the loaded quiz documents / quiz rows, after the actor's quiz
     * authorization has already been decided, so a filter can only remove a quiz
     * the actor could otherwise see — never surface one they could not.
     */
    kind: z.enum(['exam', 'quiz']).optional(),
    // Sized from the domain, not guessed: these are EXACT keys compared against the
    // stored subject / grade-level name, so a cap below the domain maximum would
    // make an otherwise-valid stored value unfilterable (request either 400s or
    // silently matches nothing).
    subject: z.string().trim().min(1).max(SUBJECT_NAME_MAX).optional(),
    gradeLevel: z.string().trim().min(1).max(GRADE_LEVEL_NAME_MAX).optional(),
    /**
     * Canonical quiz workflow status. Accepts the stored vocabulary
     * (draft/scheduled/live/closed) AND the ticket's reader-facing vocabulary
     * (draft/live/finished/archived); `quizMatchesStatusFilter` maps both onto the
     * stored shape. Multi-select (AIMSIS-19671 cycle 8) — the panel sends a
     * comma-joined list.
     */
    quizStatus: reportMultiSelect(ReportQuizStatusFilter.options).optional(),
    /** Matches the quiz owner, creator, or any shared author. */
    authorId: UserId.optional(),
    /** Matches any quiz the user is a proctor/observer of. */
    proctorId: UserId.optional(),
    /**
     * Dedicated schedule window (AIMSIS-19671 cycle 8), Exam View only.
     *
     * Overlap, not containment: a quiz matches when its own schedule window
     * intersects [scheduleFrom, scheduleTo]. A quiz whose window has no start (a
     * legacy document with only `endAt`) matches on its end alone, so a
     * missing field can never make a quiz unfilterable. Bounds are ISO
     * date-times from a `datetime-local` input and are independently optional, so
     * a caller may supply only one end.
     */
    scheduleFrom: z.string().datetime({ offset: true }).optional(),
    scheduleTo: z.string().datetime({ offset: true }).optional(),
    /**
     * Quiz-level completion classification, Exam View only, multi-select. Derived
     * from the quiz's own sessions exactly like the row's `lifecycleState`, so a
     * selected value describes the same set the table shows.
     */
    completionStatus: reportMultiSelect(ReportCompletionStatus.options).optional(),
    /**
     * Quiz-level grading summary, Exam View only, multi-select. Derived per quiz
     * from its own sessions' `awaitingEssayGrading`, so it can only narrow the
     * rows and the summary together.
     */
    gradingSummary: reportMultiSelect(ReportGradingSummary.options).optional(),
    /**
     * Participant View filters (AIMSIS-19671). Applied to the already-scoped
     * session rows after aggregation, so they narrow within the authorized set.
     */
    /** Assessment status of a participant's answers, distinct from lifecycle. */
    gradingStatus: z.enum(['awaiting', 'graded']).optional(),
    resultDeliveryStatus: z.enum(['not_applicable', 'pending', 'processing', 'retrying', 'delivered', 'failed']).optional(),
    /**
     * Exact-match session/attempt identifier. Uses the canonical `SessionId` the
     * rows themselves are validated with, so the filter's bounds cannot drift from
     * the value it exact-matches — a hand-written `min(1).max(64)` silently becomes
     * unfilterable if that type ever widens. The filter trims first, which
     * `SessionId` does not, so a padded id is still rejected rather than compared
     * against a trimmed row value.
     */
    sessionId: SessionId.trim().optional(),
    /**
     * Proctoring flag, Participant View only, multi-select (AIMSIS-19671 cycle 8).
     * Matched against the session's own `auditLog` event types; the audit trail is
     * read ONLY when this filter is supplied, so the roster's cost is unchanged for
     * every caller that does not ask for it.
     */
    proctoringFlag: reportMultiSelect(ReportProctoringFlag.options).optional(),
});
export const ReportSessionRow = z.object({
    sessionId: SessionId,
    schoolId: SchoolId,
    quizId: QuizId,
    studentId: UserId,
    classroomId: z.string().max(64).optional(),
    scheduleId: z.string().max(160).optional(),
    rawStatus: z.string(),
    status: z.string(),
    lifecycleState: ReportLifecycleState,
    startedAt: IsoTimestamp.optional(),
    submittedAt: IsoTimestamp.optional(),
    totalScore: z.number().nonnegative().optional(),
    maxScore: z.number().nonnegative().optional(),
    scorePercent: z.number().nonnegative().max(100).nullable(),
    answeredCount: z.number().int().nonnegative(),
    flaggedCount: z.number().int().nonnegative(),
    timeUsedSec: z.number().int().nonnegative(),
    awaitingEssayGrading: z.number().int().nonnegative(),
    attemptNumber: z.number().int().positive(),
    resultVersion: z.number().int().positive().nullable(),
    resultDeliveryStatus: z.enum(['not_applicable', 'pending', 'processing', 'retrying', 'delivered', 'failed']),
    resultDeliveryId: z.string().max(128).nullable(),
    resultDeliveryExternalId: z.string().max(128).nullable(),
    resultDeliveryPredecessorId: z.string().max(128).nullable(),
    resultCorrectionOfSessionId: SessionId.nullable(),
    resultCorrectionCommandId: z.string().max(128).nullable(),
    resultReconciliationRequired: z.boolean(),
    resultCorrectionReason: z.string().max(500).nullable(),
    originalSessionId: SessionId.nullable(),
    recoveryCommandId: z.string().uuid().nullable(),
    recoveryReason: z.string().max(500).nullable(),
    recoveryAt: IsoTimestamp.nullable(),
    integrityPact: z.object({
        status: z.enum(['not_required', 'acknowledged', 'missing']),
        required: z.boolean(),
        version: z.string().optional(),
        contentHash: z.string().optional(),
        acknowledgedAt: IsoTimestamp.optional(),
        acknowledgedBy: UserId.optional(),
        acknowledgedRole: z.enum(['student', 'proctor']).optional(),
    }),
    studentName: z.string().max(200).optional(),
    studentNumber: z.string().max(64).optional(),
    quizTitle: z.string().max(200).optional(),
    quizSubject: z.string().max(100).optional(),
    classroomName: z.string().max(200).optional(),
});
export const ReportQuestionAggregate = z.object({
    questionId: z.string().min(1).max(64),
    type: ReportQuestionType,
    maxScore: z.number().positive(),
    prompt: z.string().max(10000).optional(),
    correct: z.number().int().nonnegative(),
    wrong: z.number().int().nonnegative(),
    blank: z.number().int().nonnegative(),
    flagged: z.number().int().nonnegative(),
    unworked: z.number().int().nonnegative(),
    awaitingGrading: z.number().int().nonnegative(),
});
export const ReportScoreDistribution = z.object({
    under50: z.number().int().nonnegative(),
    from50To74: z.number().int().nonnegative(),
    from75To99: z.number().int().nonnegative(),
    perfect: z.number().int().nonnegative(),
});
export const ReportLifecycleCounts = z.object({
    notStarted: z.number().int().nonnegative(),
    inProgress: z.number().int().nonnegative(),
    submitted: z.number().int().nonnegative(),
    forceSubmitted: z.number().int().nonnegative(),
    awaitingGrading: z.number().int().nonnegative(),
    finalized: z.number().int().nonnegative(),
});
export const ReportSummary = z.object({
    totalParticipants: z.number().int().nonnegative(),
    participantsStarted: z.number().int().nonnegative(),
    participantsCompleted: z.number().int().nonnegative(),
    completionRate: z.number().nonnegative().max(100).nullable(),
    lifecycle: ReportLifecycleCounts,
    averageScorePercent: z.number().nonnegative().max(100).nullable(),
    averageTimeUsedSec: z.number().nonnegative().nullable(),
    scoreDistribution: ReportScoreDistribution,
    awaitingEssayGrading: z.number().int().nonnegative(),
    questionAggregates: z.array(ReportQuestionAggregate),
});
export const ReportQuizRow = z.object({
    quizId: QuizId,
    quizTitle: z.string().max(200).optional(),
    quizSubject: z.string().max(100).optional(),
    lastActivityAt: IsoTimestamp.optional(),
    lifecycleState: ReportLifecycleState,
    participantCount: z.number().int().nonnegative(),
    completionRate: z.number().nonnegative().max(100).nullable(),
    averageScorePercent: z.number().nonnegative().max(100).nullable(),
    answeredCount: z.number().int().nonnegative(),
    gradedCount: z.number().int().nonnegative(),
});
export const ReportQuizResponse = z.object({
    summary: ReportSummary.extend({ totalQuizzes: z.number().int().nonnegative() }),
    rows: z.array(ReportQuizRow),
});
export const ReportPage = z.object({
    filters: ReportQuery.extend({ from: IsoTimestamp, to: IsoTimestamp }),
    summary: ReportSummary,
    rows: z.array(ReportSessionRow),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
    /** Sessions matching the current filters (post-search, pre-pagination). */
    totalRows: z.number().int().nonnegative(),
});
export const ReportSessionDetail = z.object({
    row: ReportSessionRow,
    questions: z.array(ReportQuestionAggregate),
});
export const ReportStudentDetail = z.object({
    studentId: UserId,
    rows: z.array(ReportSessionRow),
    summary: ReportSummary,
    /**
     * Whether more attempts match than `rows` carries. The route pages at
     * `limit: 100` while `REPORT_MAX_SCAN` (250) is the ceiling above which the
     * route refuses with 413, so a participant can legitimately hold more
     * attempts than one page carries. A consumer that renders or exports `rows`
     * must be able to tell a complete list from a truncated one.
     */
    hasMore: z.boolean(),
});
export const ReportParticipantRow = z.object({
    studentId: UserId,
    studentName: z.string().max(200).optional(),
    studentNumber: z.string().max(64).optional(),
    /** Lifecycle state of the most recent attempt, or notStarted when the participant never attempted. */
    latestLifecycleState: ReportLifecycleState,
    /** Score of the most recent attempt; null when that attempt has no score yet. */
    latestScorePercent: z.number().nonnegative().max(100).nullable(),
    sessionCount: z.number().int().nonnegative(),
    lastActivityAt: IsoTimestamp.optional(),
});
export const ReportParticipantResponse = z.object({
    rows: z.array(ReportParticipantRow),
});
export const ReportQuestionRow = z.object({
    quizId: QuizId,
    quizTitle: z.string().max(200).optional(),
    questionId: z.string().min(1).max(64),
    order: z.number().int().nonnegative(),
    type: z.string().max(32),
    prompt: z.string().max(10000).optional(),
    participantCount: z.number().int().nonnegative(),
    answeredCount: z.number().int().nonnegative(),
    correctCount: z.number().int().nonnegative(),
    wrongCount: z.number().int().nonnegative(),
    blankCount: z.number().int().nonnegative(),
    flaggedCount: z.number().int().nonnegative(),
    unworkedCount: z.number().int().nonnegative(),
    awaitingGradingCount: z.number().int().nonnegative(),
    correctRate: z.number().nonnegative().max(100).nullable(),
    sessionCount: z.number().int().nonnegative().optional(),
    uniqueParticipants: z.number().int().nonnegative().optional(),
});
export const ReportQuestionResponse = z.object({
    rows: z.array(ReportQuestionRow),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    hasMore: z.boolean(),
    dataState: z.enum(['complete', 'partial', 'stale']),
    generatedAt: IsoTimestamp,
    warnings: z.array(z.string()),
});
/**
 * One row per student assigned to a quiz, aggregated across that student's
 * sessions in the quiz. `lifecycleState` mirrors the latest attempt so that
 * Not started / In progress / Awaiting grading / Finalized stay readable.
 */
export const ReportParticipantAnalysisRow = z.object({
    studentId: UserId,
    studentName: z.string().max(200).optional(),
    studentNumber: z.string().max(64).optional(),
    sessionCount: z.number().int().nonnegative(),
    answeredCount: z.number().int().nonnegative(),
    correctCount: z.number().int().nonnegative(),
    wrongCount: z.number().int().nonnegative(),
    blankCount: z.number().int().nonnegative(),
    flaggedCount: z.number().int().nonnegative(),
    awaitingEssayGrading: z.number().int().nonnegative(),
    lifecycleState: ReportLifecycleState,
    latestSessionId: SessionId.optional(),
    latestScorePercent: z.number().nonnegative().max(100).nullable(),
});
export const ReportParticipantAnalysisResponse = z.object({
    quizId: QuizId,
    quizTitle: z.string().max(200).optional(),
    rows: z.array(ReportParticipantAnalysisRow),
    total: z.number().int().nonnegative(),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
    // 'unavailable' means the roster or session scan exceeded the supported
    // bound, so no honest per-student table can be produced for this query.
    dataState: z.enum(['complete', 'partial', 'unavailable']),
    warnings: z.array(z.string()),
});
/* ---------- Item-level detail (Participant View) ---------- */
export const ReportQuestionOutcome = z.enum([
    'correct',
    'wrong',
    'blank',
    'unworked',
    'awaitingGrading',
    'graded',
]);
export const ReportQuestionDetailRow = z.object({
    questionId: z.string().min(1).max(64),
    order: z.number().int().nonnegative(),
    type: z.string().max(32),
    prompt: z.string().max(10000),
    response: z.string().max(10000).nullable(),
    outcome: ReportQuestionOutcome,
    score: z.number().nonnegative().nullable(),
    maxScore: z.number().nonnegative(),
    flagged: z.boolean(),
    feedback: z.string().max(2000).nullable(),
});
export const ReportQuestionDetailResponse = z.object({
    sessionId: SessionId,
    studentId: UserId,
    quizId: QuizId,
    quizTitle: z.string().max(200).optional(),
    rows: z.array(ReportQuestionDetailRow),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    hasMore: z.boolean(),
    dataState: z.enum(['complete', 'partial', 'stale']),
    generatedAt: IsoTimestamp,
    warnings: z.array(z.string()),
});
export const ReportDetailPagination = z.object({
    page: z.coerce.number().int().min(1).max(500).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
export const ReportItemAnalysisResponse = z.object({
    n: z.number().int().nonnegative(),
    k: z.number().int().nonnegative(),
    meanTotalPercent: z.number().finite().nullable(),
    standardDeviation: z.number().finite().nullable(),
    kr20: z.number().finite().nullable(),
    items: z.array(z.object({
        questionId: z.string(),
        type: z.string(),
        prompt: z.string(),
        maxScore: z.number().nonnegative(),
        competency: z.string(),
        facilityIndex: z.number().finite().nullable(),
        discriminationIndex: z.number().finite().nullable(),
        blankCount: z.number().int().nonnegative(),
        distractors: z.array(z.object({
            id: z.string(), text: z.string(), isCorrect: z.boolean(),
            count: z.number().int().nonnegative(), percent: z.number().finite().nullable(),
        })),
    })),
    competencies: z.array(z.object({
        competency: z.string(), itemCount: z.number().int().nonnegative(), percent: z.number().finite().nullable(),
    })),
});
//# sourceMappingURL=reports.js.map